import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * PinesMultiSelect
 *
 * Props:
 *  - items: { id, label }[]
 *  - value: number[]
 *  - onChange: (ids: number[]) => void
 *  - loading?: boolean
 *  - error?: string
 *  - disabled?: boolean
 */
export default function PinesMultiSelect({
  items = [],
  value = [],
  onChange = () => {},
  loading = false,
  error = "",
  disabled = false,
}) {
  const ref = useRef(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // =========================
  // click afuera
  // =========================
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, []);

  // =========================
  // filtro
  // =========================
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter((i) =>
      `${i.id} ${i.label}`.toLowerCase().includes(q)
    );
  }, [items, search]);

  // =========================
  // label
  // =========================
  const label =
    value.length === 0
      ? "Seleccionar"
      : value.length === 1
      ? "1 seleccionado"
      : `${value.length} seleccionados`;

  // =========================
  // handlers
  // =========================
  const toggle = (id) =>
    onChange(
      value.includes(id)
        ? value.filter((x) => x !== id)
        : [...value, id]
    );

  const marcarTodas = () => onChange(filtered.map((f) => f.id));

  const desmarcarTodas = () =>
    onChange(value.filter((id) => !filtered.some((f) => f.id === id)));

  // =========================
  // RENDER (MISMO UI)
  // =========================
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        className="input w-full flex items-center text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">{label}</span>
        <span className="ml-auto">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl">
          {/* HEADER */}
          <div className="p-2 border-b bg-gray-50 flex flex-wrap gap-2">
            <input
              placeholder="Filtrar registro"
              className="flex-1 border rounded px-2 py-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <button
              type="button"
              onClick={marcarTodas}
              className="text-green-700 text-sm"
              disabled={loading}
            >
              ✓ Marcar todas
            </button>

            <button
              type="button"
              onClick={desmarcarTodas}
              className="text-green-700 text-sm"
              disabled={loading}
            >
              ✗ Desmarcar todas
            </button>
          </div>

          {/* LISTA */}
          <div className="max-h-56 overflow-auto">
            {loading && (
              <div className="px-3 py-2 text-sm text-gray-500">
                Cargando…
              </div>
            )}

            {!loading && error && (
              <div className="px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            )}

            {!loading &&
              filtered.map((op) => (
                <label
                  key={op.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(op.id)}
                    onChange={() => toggle(op.id)}
                  />
                  <span className="truncate">{op.label}</span>
                </label>
              ))}

            {!loading && filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                Sin coincidencias
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="p-2 border-t text-right bg-white">
            <button
              type="button"
              className="text-green-700"
              onClick={() => setOpen(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
